import React, { useState, useEffect } from "react"
import {
  RoundedButton,
  Input,
  CustomMaskedInput,
  StdModal,
} from "@omnichannel/egr-common-ui"
import styled from "styled-components"
import DateAndTimePicker, { EgrDatePicker } from "./dateAndTimePicker"
import CelebrantOptionBox from "./celebrantOptionBox"
import DeliveryOptionBox from "./deliveryOptionBox"
import { APP_ID } from "../../helpers/constants"
import LoyaltyForm from "./loyaltyForm"
import DeliveryAddressForm from "./deliveryAddressForm"
import { parseISO, differenceInCalendarDays, isPast, sub } from "date-fns"
import { useQuery } from "@apollo/client"
import { longlatByPlaceIdQuery } from "../gql/wishlist"

const MainBox = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`
const DetailsBoxWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
`

const DetailsBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  padding: 0 0.4rem;
  @media (min-width: 501px) {
    width: 80%;
  }
  @media (min-width: 601px) {
    width: 70%;
  }
  @media (min-width: 768px) {
    width: 32%;
  }
  margin-bottom: 2rem;
`

const DetailsHeaderBox = styled.div`
  display: flex;
  width: auto;
  justify-content: center;
  align-items: center;
  background-color: #7ac2d7;
  color: #fff;
  padding: 1.2rem 1.6rem;
  font-size: 1.4rem;
  margin-bottom: 2.4rem;
  border-radius: 2rem;
  user-select: none;
`

const DetailsContentBox = styled.div`
  flex-direction: column;
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: flex-start;
`

const InputBox = styled.div`
  display: flex;
  width: 100%;
  margin-top: ${props => (props.marginTop ? props.marginTop : "")};
  margin-bottom: ${props =>
    props.marginBottom ? props.marginBottom : ".8rem"};
  z-index: ${props => (props.zIndexValue ? props.zIndexValue : "0")};
`

const ButtonBox = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 1rem 0 0 0;
`

const AddLoyaltyBox = styled.div`
  display: flex;
  width: 40%;
  justify-content: flex-end;
  align-items: center;
  padding: 0 0 0 0.4rem;
  transform: translateY(0.4rem);
`

const InputAutoCompleteOff = styled(Input).attrs({
  autoComplete: "off",
})``

const EventTypes = [
  { id: "babyshower", value: "Baby Shower", code: 1 },
  { id: "baptism", value: "Baptism", code: 2 },
  { id: "birthday_adult", value: "Birthday-Adult", code: 3 },
  { id: "birthday_kid", value: "Birthday-Kids", code: 4 },
  { id: "birthday_debut", value: "Birthday-Debut", code: 5 },
  { id: "wedding", value: "Wedding", code: 6 },
  { id: "anniversary", value: "Anniversary", code: 7 },
  { id: "housewarming", value: "House Warming", code: 8 },
  { id: "others", value: "Others", code: 9 },
]

export const getEventCodeForType = type => {
  const event = EventTypes.find(event => event.id === type)
  return event.code
}

export const getEventLabelForType = type => {
  const event = EventTypes.find(event => event.id === type)
  return event.value
}

function EventForm(props) {
  const { userInfo = {}, wishlist = {} } = props
  const isRegistrantIsCelebrant = wishlist._id
    ? `${userInfo.firstName}${userInfo.lastName}` ===
      `${wishlist.celebrantFirstName}${wishlist.celebrantLastName}`
    : true
  const subTypeValue = wishlist._id
    ? EventTypes.find(eventType => eventType.id === wishlist.subType).value
    : ""

  const defaultFormValues = {
    name: wishlist._id ? wishlist.name : "",
    summary: wishlist._id ? wishlist.summary : "",
    subtype: { id: wishlist.subType, value: subTypeValue },
    venue: wishlist._id ? wishlist.eventAddress : "",
    firstName: wishlist._id
      ? wishlist.celebrantFirstName
        ? wishlist.celebrantFirstName
        : ""
      : userInfo.firstName,
    lastName: wishlist._id
      ? wishlist.celebrantLastName
        ? wishlist.celebrantLastName
        : ""
      : userInfo.lastName,
    email: wishlist._id
      ? wishlist.eventEmail
        ? wishlist.eventEmail
        : ""
      : userInfo.email,
    contactNumber: wishlist._id ? wishlist.eventContact : "",
    registrantIsCelebrant: isRegistrantIsCelebrant,
    isPickup: wishlist.isPickup,
    deliveryAddress: wishlist._id ? wishlist.deliveryAddress : "",
    deliveryAddress2: wishlist._id ? wishlist.deliveryAddress2 : "",
    branch: { id: "", value: "" },
    loyaltyCard: wishlist._id
      ? { id: wishlist.loyaltyCard, value: wishlist.loyaltyCard }
      : { id: "", value: "" },
    lng: wishlist._id ? wishlist.lng : "",
    lat: wishlist._id ? wishlist.lat : "",
  }

  const disabled =
    wishlist._id &&
    (isPast(new Date(wishlist.eventDate)) || wishlist.status === "CANCELLED"
      ? true
      : false)

  // console.log("disabled?", disabled)

  const [formValues, setFormValues] = useState(defaultFormValues)
  const [errors, setErrors] = useState({})
  const [openLoyaltyModal, setOpenLoyaltyModal] = useState(false)
  const [selectedDate, setSelectedDate] = useState(
    wishlist._id ? parseISO(wishlist.eventDate) : null
  )
  const [selectedDateDelivery, setSelectedDateDelivery] = useState(
    wishlist._id
      ? !wishlist.isPickup
        ? parseISO(wishlist.deliveryDate)
        : null
      : null
  )
  const [selectedDatePickup, setSelectedDatePickup] = useState(
    wishlist._id
      ? wishlist.isPickup
        ? parseISO(wishlist.deliveryDate)
        : null
      : null
  )
  const [registrantIsCelebrant, setRegistrantIsCelebrant] = useState(
    isRegistrantIsCelebrant
  )
  const [isDelivery, setIsDelivery] = useState(
    wishlist._id ? !wishlist.isPickup : true
  )
  const [placeId, setPlaceId] = useState(null)

  const daysToGoEvent =
    wishlist._id &&
    wishlist.eventDate &&
    differenceInCalendarDays(new Date(wishlist.eventDate), new Date())

  const daysToGoDelivery =
    wishlist._id &&
    !wishlist.isPickup &&
    differenceInCalendarDays(new Date(wishlist.deliveryDate), new Date())

  const daysToGoPickup =
    wishlist._id &&
    wishlist.isPickup &&
    differenceInCalendarDays(new Date(wishlist.deliveryDate), new Date())

  const longLatResult = useQuery(longlatByPlaceIdQuery, {
    variables: { placeId },
    skip: !placeId,
    fetchPolicy: "network-only",
  })

  const handleChange = event => {
    const { id, value } = event.target
    setFormValues({ ...formValues, [id]: value })
    setErrors({ ...errors, [id]: "" })
  }

  if (
    longLatResult &&
    longLatResult.data &&
    longLatResult.data.longlatByPlaceId &&
    !longLatResult.loading
  ) {
    const { lng, lat } = longLatResult.data.longlatByPlaceId
    if (formValues.lng !== lng) {
      setFormValues({ ...formValues, lng })
    }
    if (formValues.lat !== lat) {
      setFormValues({ ...formValues, lat })
    }
  }

  //sideEffect initialization
  useEffect(() => {
    // console.log("here in props.branches effect...")
    if (props.branches.length > 0 && wishlist._id && wishlist.isPickup) {
      let branchValue = {}
      if (wishlist._id) {
        branchValue = props.branches.find(
          branch => branch.id == wishlist.branch
        )
      }
      const branchInitialValue = branchValue ? branchValue.value : ""
      const branch = { id: wishlist.branch, value: branchInitialValue }
      // console.log("ok good", branch)
      setFormValues({ ...formValues, branch })
    } else {
      // console.log("ooops me wait...")
    }
  }, [props.branches])

  const handleSave = () => {
    let {
      name,
      subtype,
      venue,
      firstName,
      lastName,
      email,
      contactNumber,
      isPickup,
      deliveryAddress,
      deliveryAddress2,
      branch,
      lng,
      lat,
    } = formValues

    let formErrors = {}
    if (!name) {
      formErrors.name = "required"
    }
    if (!subtype.id) {
      formErrors.subtype = "required"
    }
    if (!venue) {
      formErrors.venue = "required"
    }
    if (!firstName) {
      formErrors.firstName = "required"
    }
    if (!lastName) {
      formErrors.lastName = "required"
    }
    if (!email) {
      formErrors.email = "required"
    }
    if (!contactNumber) {
      formErrors.contactNumber = "required"
    } else {
      if (cleanMobileNumber(contactNumber).length < 11) {
        formErrors.contactNumber = "Invalid mobile number"
      }
    }

    if (isDelivery) {
      isPickup = false
    } else {
      isPickup = true
    }

    if (!isPickup && !deliveryAddress) {
      formErrors.deliveryAddress = "required"
    }
    if (!isDelivery && !branch.id) {
      formErrors.branch = "required"
    }

    if (!selectedDate) {
      formErrors.selectedDate = "required"
    }

    if (!isPickup && !selectedDateDelivery) {
      formErrors.selectedDateDelivery = "required"
    }

    if (isPickup && !selectedDatePickup) {
      formErrors.selectedDatePickup = "required"
    }

    if (isDelivery && errors.selectedDateDeliveryAlias)
      formErrors.selectedDateDeliveryAlias = errors.selectedDateDeliveryAlias

    if (!isDelivery && errors.selectedDatePickupAlias)
      formErrors.selectedDatePickupAlias = errors.selectedDatePickupAlias

    // console.log("errors? ", formErrors)

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors)
    } else {
      const wishlist = {}
      if (props.wishlist._id) {
        wishlist._id = props.wishlist._id
      }
      wishlist.app = APP_ID
      wishlist.name = formValues.name
      // wishlist.branch = formValues.branch.id
      wishlist.subType = formValues.subtype.id
      wishlist.celebrantFirstName = formValues.firstName
      wishlist.celebrantLastName = formValues.lastName
      wishlist.nameInGuestCard = formValues.preferredName
      wishlist.summary = formValues.summary
      wishlist.type = "EVENT"
      wishlist.eventDate = selectedDate
      wishlist.eventAddress = formValues.venue
      wishlist.eventContact = cleanMobileNumber(formValues.contactNumber)
      wishlist.eventEmail = formValues.email
      wishlist.createdDate = new Date()
      wishlist.loyaltyCard = formValues.loyaltyCard.id
      wishlist.isPublic = true

      if (isPickup) {
        selectedDatePickup.setHours(0, 0, 0)
        wishlist.isPickup = true
        wishlist.deliveryDate = selectedDatePickup
        wishlist.branch = formValues.branch.id
        wishlist.deliveryAddress = ""
        wishlist.deliveryAddress2 = ""
      } else {
        selectedDateDelivery.setHours(0, 0, 0)
        wishlist.isPickup = false
        wishlist.deliveryAddress = formValues.deliveryAddress
        wishlist.deliveryAddress2 = formValues.deliveryAddress2
        wishlist.deliveryDate = selectedDateDelivery
        wishlist.lng = formValues.lng
        wishlist.lat = formValues.lat
      }

      // console.log("wishlist to save::", wishlist)
      props.saveWishlist({
        variables: {
          wishlist,
        },
      })
    }
  }

  const cleanMobileNumber = number => {
    if (!number) {
      return ""
    }
    return number
      .split("-")
      .join("")
      .replace(/#/g, "")
  }

  const handleRegistrantIsCelebrant = selection => {
    if (selection === "Yes") {
      setRegistrantIsCelebrant(true)
      setFormValues({
        ...formValues,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
        preferredName: `${userInfo.firstName} ${userInfo.lastName}`,
      })
    } else {
      setRegistrantIsCelebrant(false)
      setFormValues({
        ...formValues,
        firstName: "",
        lastName: "",
        email: "",
        preferredName: "",
      })
    }
  }

  const dynamicMinDateDeliveryOrPickup =
    selectedDate && sub(new Date(selectedDate), { days: 1 })

  return (
    <MainBox>
      <DetailsBoxWrapper>
        <DetailsBox>
          <DetailsHeaderBox>Event Details</DetailsHeaderBox>
          <DetailsContentBox>
            <InputBox>
              <Input
                label="Event Name *"
                placeholder="Name"
                value={formValues.name}
                id="name"
                onChange={handleChange}
                error={errors.name}
                disabled={disabled}
              />
            </InputBox>
            <InputBox>
              <Input
                label="Event Description"
                placeholder="Summary"
                value={formValues.summary}
                id="summary"
                onChange={handleChange}
                error={errors.summary}
                disabled={disabled}
              />
            </InputBox>
            <InputBox zIndexValue={"100"}>
              <InputAutoCompleteOff
                disabled={disabled}
                width="100%"
                options={!disabled && EventTypes}
                label="Event Type *"
                placeholder="Type"
                dropdownMaxHeight={"20rem"}
                id="subtype"
                value={formValues.subtype.value}
                defaultSelectedOptionId={formValues.subtype.id}
                onClickOption={option => {
                  setFormValues({
                    ...formValues,
                    subtype: option,
                  })
                  setErrors({ ...errors, subtype: "" })
                }}
                onClear={() => {
                  setFormValues({
                    ...formValues,
                    subtype: { id: "", value: "" },
                  })
                  setErrors({ ...errors, subtype: "" })
                }}
                error={errors.subtype}
              />
            </InputBox>
            <InputBox>
              <DateAndTimePicker
                disabled={disabled || (wishlist._id && daysToGoEvent <= 3)}
                minDate={props.minEventDate}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                errors={errors}
                setErrors={setErrors}
              />
            </InputBox>
            <InputBox>
              <Input
                disabled={disabled}
                label="Venue *"
                placeholder="Venue"
                value={formValues.venue}
                id="venue"
                onChange={handleChange}
                error={errors.venue}
              />
            </InputBox>
          </DetailsContentBox>
        </DetailsBox>
        <DetailsBox>
          <DetailsHeaderBox>Celebrant Details</DetailsHeaderBox>
          <DetailsContentBox>
            <InputBox>
              <CelebrantOptionBox
                disabled={disabled}
                registrantIsCelebrant={registrantIsCelebrant}
                handleSelection={handleRegistrantIsCelebrant}
              />
            </InputBox>

            <InputBox>
              <Input
                disabled={disabled}
                label="Celebrant's First Name *"
                placeholder="First Name"
                value={formValues.firstName}
                id="firstName"
                onChange={handleChange}
                error={errors.firstName}
              />
            </InputBox>

            <InputBox>
              <Input
                disabled={disabled}
                label="Celebrant's Last Name *"
                placeholder="Last Name"
                value={formValues.lastName}
                id="lastName"
                onChange={handleChange}
                error={errors.lastName}
              />
            </InputBox>
            <InputBox>
              <CustomMaskedInput
                disabled={disabled}
                label="Mobile number *"
                maskChar="#"
                mask="0000-000-0000"
                placeholder="0000-000-0000"
                value={formValues.contactNumber}
                onChange={event => {
                  const contactNumberEvent = { target: {} }
                  contactNumberEvent.target = {}
                  contactNumberEvent.target.value = event.target.value
                  contactNumberEvent.target.id = "contactNumber"
                  handleChange(contactNumberEvent)
                }}
                id="contactNumber"
                error={errors.contactNumber}
                // style={{ marginTop: ".1rem", }}
                inputPadding={"1.2rem 1.2rem"}
              />
            </InputBox>
            <InputBox marginTop={".6rem"}>
              <Input
                disabled={disabled}
                label="Email *"
                placeholder="Email"
                value={formValues.email}
                id="email"
                onChange={handleChange}
                error={errors.email}
              />
            </InputBox>
          </DetailsContentBox>
        </DetailsBox>
        <DetailsBox>
          <DetailsHeaderBox>Gift Delivery Details</DetailsHeaderBox>
          <DetailsContentBox>
            <InputBox>
              <DeliveryOptionBox
                disabled={
                  disabled ||
                  // (wishlist._id &&
                  //   (daysToGoDelivery <= 2 || daysToGoPickup <= 2))
                  (wishlist._id && daysToGoEvent <= 3)
                }
                setIsDelivery={setIsDelivery}
                isDelivery={isDelivery}
              />
            </InputBox>
            {isDelivery ? (
              <>
                <InputBox zIndexValue="100">
                  <DeliveryAddressForm
                    setPlaceId={setPlaceId}
                    setFormValues={setFormValues}
                    formValues={formValues}
                    errors={errors}
                    setErrors={setErrors}
                    // disabled={disabled}
                    disabled={disabled || (wishlist._id && daysToGoEvent <= 3)}
                  />
                </InputBox>
                <InputBox>
                  <Input
                    disabled={disabled}
                    label="Address Details"
                    placeholder="House No / Street / Unit No / Building"
                    value={formValues.deliveryAddress2}
                    id="deliveryAddress2"
                    onChange={handleChange}
                    error={errors.deliveryAddress2}
                  />
                </InputBox>
                <InputBox marginBottom={".2rem"}>
                  <EgrDatePicker
                    disabled={disabled || (wishlist._id && daysToGoEvent <= 3)}
                    // disabled={
                    //   disabled || (wishlist._id && daysToGoDelivery <= 2)
                    // }
                    selectedDate={selectedDateDelivery}
                    setSelectedDate={setSelectedDateDelivery}
                    label={"Delivery Date *"}
                    error={errors.selectedDateDelivery}
                    errors={errors}
                    setErrors={setErrors}
                    errorFieldAlias={"selectedDateDeliveryAlias"}
                    errorFieldForm={"selectedDateDelivery"}
                    minDate={
                      selectedDate
                        ? dynamicMinDateDeliveryOrPickup
                        : props.minDeliveryOrPickupDate
                    }
                    maxDate={props.defaultMaxDeliveryOrPickupDate(selectedDate)}
                    maxDateMessage={"Maximum 9 days after event date"}
                    minDateMessage={"Minimum 1 day before the event date"}
                  />
                </InputBox>
              </>
            ) : (
              <InputBox marginBottom={".1rem"}>
                <EgrDatePicker
                  disabled={disabled || (wishlist._id && daysToGoEvent <= 3)}
                  // disabled={disabled || (wishlist._id && daysToGoPickup <= 2)}
                  selectedDate={selectedDatePickup}
                  setSelectedDate={setSelectedDatePickup}
                  label={"Preferred Pick Up Date *"}
                  error={errors.selectedDatePickup}
                  errors={errors}
                  setErrors={setErrors}
                  errorFieldAlias={"selectedDatePickupAlias"}
                  errorFieldForm={"selectedDatePickup"}
                  minDate={
                    selectedDate
                      ? dynamicMinDateDeliveryOrPickup
                      : props.minDeliveryOrPickupDate
                  }
                  maxDate={props.defaultMaxDeliveryOrPickupDate(selectedDate)}
                  maxDateMessage={"Maximum 9 days after event date"}
                  minDateMessage={"Minimum 1 day before the event date"}
                />
              </InputBox>
            )}

            {!isDelivery && (
              <InputBox zIndexValue="100" marginBottom={".9rem"}>
                <InputAutoCompleteOff
                  disabled={disabled || (wishlist._id && daysToGoEvent <= 3)}
                  width="100%"
                  options={
                    !(disabled || (wishlist._id && daysToGoEvent <= 3)) &&
                    props.branches
                  }
                  label="Branch *"
                  placeholder="Branch"
                  dropdownMaxHeight={"20rem"}
                  value={formValues.branch.value}
                  defaultSelectedOptionId={formValues.branch.id}
                  onClickOption={option => {
                    setFormValues({
                      ...formValues,
                      branch: option,
                    })
                    setErrors({ ...errors, branch: "" })
                  }}
                  onClear={() => {
                    setFormValues({
                      ...formValues,
                      branch: { id: "", value: "" },
                    })
                    setErrors({ ...errors, branch: "" })
                  }}
                  error={errors.branch}
                />
              </InputBox>
            )}
            <InputBox>
              <InputAutoCompleteOff
                disabled={disabled}
                options={!disabled && props.loyalties}
                label="Earn loyalty card points!"
                placeholder="SMAC / Prestige "
                // value={formValues.loyaltyCard}
                id="loyaltyCard"
                // onChange={handleChange}
                error={errors.loyatltyCard}
                dropdownMaxHeight={"20rem"}
                value={formValues.loyaltyCard.value}
                defaultSelectedOptionId={formValues.loyaltyCard.id}
                onClickOption={option => {
                  setFormValues({
                    ...formValues,
                    loyaltyCard: option,
                  })
                  setErrors({ ...errors, loyaltyCard: "" })
                }}
                onClear={() => {
                  setFormValues({
                    ...formValues,
                    loyaltyCard: { id: "", value: "" },
                  })
                  setErrors({ ...errors, loyaltyCard: "" })
                }}
              />
              <AddLoyaltyBox>
                <RoundedButton
                  disabled={disabled}
                  margin={"0 0 0 .8rem"}
                  padding={".8rem .8rem"}
                  width="8rem"
                  color={"#5284bd"}
                  textColor={"#FFF"}
                  onClick={() => {
                    setOpenLoyaltyModal(true)
                  }}
                  disabledColor={"#CCCCCC"}
                  borderColor={
                    props.saveWishlistLoading || disabled
                      ? "#CCCCCC"
                      : "#5284bd"
                  }
                >
                  Add
                </RoundedButton>
              </AddLoyaltyBox>
            </InputBox>
          </DetailsContentBox>
        </DetailsBox>
      </DetailsBoxWrapper>
      <ButtonBox>
        <RoundedButton
          margin={"0 .8rem 0 .8rem"}
          width="100px"
          primary
          onClick={handleSave}
          disabled={props.saveWishlistLoading || disabled}
          disabledColor={"#CCCCCC"}
          color="#2f2f2f"
          borderColor={
            props.saveWishlistLoading || disabled ? "#CCCCCC" : "#2f2f2f"
          }
        >
          {props.saveWishlistLoading ? "Saving..." : "Save"}
        </RoundedButton>
        <RoundedButton
          disabled={disabled}
          margin={"0 .8rem 0 .8rem"}
          width="100px"
          primary
          onClick={() => {
            setFormValues(defaultFormValues)
            setIsDelivery(wishlist._id ? !wishlist.isPickup : true)
            setErrors({})
            setSelectedDate(wishlist._id ? parseISO(wishlist.eventDate) : null)
            setSelectedDateDelivery(
              wishlist._id
                ? !wishlist.isPickup
                  ? parseISO(wishlist.deliveryDate)
                  : null
                : null
            )
            setSelectedDatePickup(
              wishlist._id
                ? wishlist.isPickup
                  ? parseISO(wishlist.deliveryDate)
                  : null
                : null
            )
          }}
          color="#2f2f2f"
          disabledColor={"#CCCCCC"}
          borderColor={
            props.saveWishlistLoading || disabled ? "#CCCCCC" : "#2f2f2f"
          }
        >
          Reset
        </RoundedButton>
        <RoundedButton
          margin={"0 .8rem 0 .8rem"}
          width="100px"
          primary
          onClick={props.onCancel}
        >
          Cancel
        </RoundedButton>
      </ButtonBox>
      <StdModal
        showModal={openLoyaltyModal}
        closeModal={() => {
          setOpenLoyaltyModal(false)
        }}
        closeOnBackgroundTouch={true}
        title={"Add Loyalty"}
        body={
          <LoyaltyForm
            openNotification={props.openNotification}
            setOpenLoyaltyModal={setOpenLoyaltyModal}
          />
        } //optional
        isFullScreenOnMobile={false}
        isFullScreen={false}
        centerContent={true}
      />
    </MainBox>
  )
}

export default EventForm
